import CasesApi from "@/api/cases";
import { convertInquiry } from "@/api/inquiries";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import {
  FormDescription,
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import {
  CaseStatusSelector,
  CasePrioritySelector,
  CaseTypeSelector,
  CocIntakeForm,
  Signature,
  UserSelector,
} from "@/Features";
import { useForm, zodResolver } from "@mantine/form";
import {
  Button,
  Divider,
  FormSection,
  Grid,
  Option,
  Switch,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";
import { useSnackbar } from "notistack";
import { useState } from "react";
import z from "zod";

export const ConvertInquiryFormDataSchema = z.object({
  case_number: z.string().optional(),
  case_ref: z.string({ message: "Case name is required" }),
  case_lead: z.object({ label: z.string(), value: z.number(), data: z.any() }),
  case_lead_id: z.number().optional(),
  inquiry_id: z.number({ message: "Inquiry is required" }),
  import_evidence: z.boolean(),
  import_people: z.boolean(),
  import_docs: z.boolean(),
  include_coc: z.boolean(),
  coc: z
    .object({
      timestamp: z
        .string({ message: "Timestamp is required" })
        .min(1, { message: "Timestamp is required" }),
      custody_from: z
        .string({ message: "Received from is required" })
        .min(1, { message: "Received from is required" }),
      custody_from_sig: z.any(),
      custody_to_sig: z.any(),
      location: z
        .object({
          location_id: z
            .number({ message: "Received by is required" })
            .min(1, { message: "Received by is required" }),
        })
        .catchall(z.any()),
      location_received: z.string().optional(),
      reason: z.string().optional(),
    })
    .optional(),
  evidence_number_import: z.boolean(),
});

export type ConvertInquiryFormData = {
  inquiry_id: number;
  case_number: string;
  case_ref: string;
  case_lead?: Option;
  case_lead_id?: number;
  case_type?: string;
  case_status?: string;
  case_description?: string;
  priority_id?: number;
  import_evidence: boolean;
  import_people: boolean;
  import_docs: boolean;
  evidence_number_import: boolean;
  include_coc: boolean;
  coc?: {
    timestamp: string; // date/time of intake
    custody_from: string; // name of person that provided the items
    signature: {
      custody_to_sig: Signature; // signature of the person that received the items
      custody_from_sig: Signature; // signature of the person that provided the items
    }; // signature of the person that provided the items
    location: { location_id: number }; // location id of the location that received the items
    location_received: string; // location name of the location that received the items
    reason: string; // reason for the intake
  };
};

export type ConvertInquiryFormProps = {
  inquiry: {
    inquiry_id: number;
    inquiry_case_num: string;
    request_name: string;
  };
  onSubmit: (data: ConvertInquiryFormData) => void;
  onCancel: () => void;
  defaultFormData?: Partial<ConvertInquiryFormData>;
};

export const ConvertInquiryForm: React.FC<ConvertInquiryFormProps> = ({
  inquiry,
  onSubmit,
  onCancel,
  defaultFormData,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const form = useForm({
    initialValues: {
      inquiry_id: inquiry?.inquiry_id,
      case_number: "",
      case_ref: inquiry?.request_name,
      case_lead: defaultFormData?.case_lead,
      case_lead_id: defaultFormData?.case_lead?.value,
      case_type: defaultFormData?.case_type,
      case_status: defaultFormData?.case_status || "Active",
      case_description: defaultFormData?.case_description,
      import_evidence: defaultFormData?.import_evidence ?? true,
      import_people: defaultFormData?.import_people ?? true,
      import_docs: defaultFormData?.import_docs ?? true,
      include_coc: defaultFormData?.include_coc || false,
      coc: defaultFormData?.coc,
      evidence_number_import: defaultFormData?.evidence_number_import || false,
    },
    validate: zodResolver(ConvertInquiryFormDataSchema),
  });

  const formData = form.getValues();

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const isCaseNumberAvailable = async (value: string) => {
    const result = await CasesApi.caseNumberExists({
      case_number: value,
    });

    return !result;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const validateResult = form.validate();

    if (validateResult.hasErrors) {
      enqueueSnackbar(
        `Please complete all required fields. ${JSON.stringify(
          Object.values(validateResult.errors)
        )}`,
        {
          variant: "error",
        }
      );
      setSubmitting(false);
      return;
    }

    const formData = form.getValues();

    if (
      formData.case_number &&
      !(await isCaseNumberAvailable(formData.case_number))
    ) {
      form.setFieldError("case_number", "Case Number already exists");
      enqueueSnackbar("Case Number already exists", {
        variant: "error",
      });
      setSubmitting(false);
      return;
    }

    form.clearErrors();

    delete formData.case_lead;

    formData.case_number = formData.case_number.trim();
    formData.case_ref = formData.case_ref.trim();

    await convertInquiry(inquiry.inquiry_id, formData);

    enqueueSnackbar(`Inquiry Converted.`, {
      variant: "success",
    });

    onSubmit?.({ ...formData, inquiry_id: inquiry.inquiry_id });
    setSubmitting(false);
  };

  return (
    <FormWrapper width={600}>
      <div>
        <FormTitle>Convert Inquiry</FormTitle>
        <FormDescription>
          Convert this inquiry into a full Monolith case.
        </FormDescription>
      </div>
      <FormSection allowCollapse={false} title="Case Details">
        <Grid col={2}>
          <TextInput
            label="Case Number"
            placeholder="Leave blank to auto-generate"
            description="This is the unique identifier for the case - Monolith will ensure that a globally unique value is used."
            disabled={submitting}
            key={form.key("case_number")}
            {...form.getInputProps("case_number")}
          />
          <TextInput
            label="Case Name"
            placeholder="Provide a short and memorable name"
            description="This is the name of the case that will be displayed in the case list. Great case names are short and memorable."
            disabled={submitting}
            key={form.key("case_ref")}
            {...form.getInputProps("case_ref")}
          />
          <UserSelector
            label="Case Lead"
            placeholder="Select the case lead"
            description="The user who will be the lead of the case."
            query={{ includeObservers: false, includeInactive: false }}
            disabled={submitting}
            key={form.key("case_lead")}
            clearable={true}
            {...form.getInputProps("case_lead")}
            onChange={(_, option) => {
              form.setFieldValue("case_lead", option);
              form.setFieldValue("case_lead_id", option?.value);
            }}
          />
          <CaseTypeSelector
            label="Type"
            placeholder="Select the case type"
            description="The type of case to convert the inquiry into"
            disabled={submitting}
            clearable={true}
            key={form.key("case_type")}
            {...form.getInputProps("case_type")}
          />
          <CaseStatusSelector
            label="Status"
            placeholder="Select the case status"
            description="The status of the case to convert the inquiry into"
            disabled={submitting}
            clearable={true}
            key={form.key("case_status")}
            {...form.getInputProps("case_status")}
          />
          <CasePrioritySelector
            label="Priority"
            placeholder="Select the case priority"
            description="The priority of the case to convert the inquiry into"
            disabled={submitting}
            clearable={true}
            key={form.key("priority_id")}
            {...form.getInputProps("priority_id")}
          />
          <TextAreaInput
            colSpan={2}
            label="Case Description"
            placeholder="Enter a brief summary of the case"
            description="The description of the case to convert the inquiry into"
            disabled={submitting}
            key={form.key("case_description")}
            {...form.getInputProps("case_description")}
          />
        </Grid>
      </FormSection>
      <FormSection allowCollapse={false} title="Convert Options">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 20,
          }}
        >
          <Switch
            defaultValue={formData.import_evidence}
            onChange={(value) => {
              form.setFieldValue("import_evidence", value);
            }}
            label="Import Evidence"
            description="Import evidence from the inquiry into the case"
            size="xs"
            disabled={submitting}
          />
          <Switch
            defaultValue={formData.import_people}
            onChange={(value) => {
              form.setFieldValue("import_people", value);
            }}
            label="Import People"
            description="Import people from the inquiry into the case"
            disabled={submitting}
          />
          <Switch
            defaultValue={formData.import_docs}
            onChange={(value) => {
              form.setFieldValue("import_docs", value);
            }}
            label="Import Documents"
            description="Import documents from the inquiry into the case"
            disabled={submitting}
          />
        </div>
      </FormSection>
      {formData.import_evidence && (
        <FormSection
          defaultOpen={false}
          allowCollapse={true}
          title="Evidence Options"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <div>
                  <h4>Evidence Numbers</h4>
                  <FormDescription style={{ fontSize: 12, marginBottom: 10 }}>
                    Import evidence numbers from the inquiry into the case,
                    otherwise auto-generate new evidence numbers.
                  </FormDescription>
                </div>
                <Switch
                  defaultValue={formData.evidence_number_import}
                  onChange={(value) => {
                    form.setFieldValue("evidence_number_import", value);
                  }}
                  label="Import Evidence Numbers"
                  description="Import evidence numbers from the inquiry into the case, otherwise auto-generate new evidence numbers."
                  disabled={submitting}
                />
              </div>
              <div>
                <div>
                  <h4>Chain of Custody</h4>
                  <FormDescription style={{ fontSize: 12, marginBottom: 10 }}>
                    Record the initial chain of custody for the evidence items
                    that will be merged into the case.
                  </FormDescription>
                </div>
                <Switch
                  defaultValue={formData.include_coc}
                  onChange={(value) => {
                    form.setFieldValue("include_coc", value);
                  }}
                  label="Include Chain of Custody"
                  description="Include the chain of custody in the merge."
                  disabled={submitting}
                />
              </div>
            </div>
            {formData.include_coc && (
              <>
                <Divider />
                <div>
                  <div>
                    <h4>Handle Chain of Custody</h4>
                    <FormDescription style={{ fontSize: 12, marginBottom: 10 }}>
                      Create the initial chain of custody record for the
                      evidence.
                    </FormDescription>
                  </div>
                  <CocIntakeForm
                    showButtonMenu={false}
                    disabled={submitting}
                    onChange={(values) => {
                      form.setFieldValue("coc", {
                        timestamp: values.timestamp,
                        signature: {
                          custody_from_sig: values.custody_from_sig,
                          custody_to_sig: values.custody_to_sig,
                        },
                        custody_from: values.custody_from,
                        location: values.custody_to as unknown as {
                          location_id: number;
                        },
                        location_received: values.location_received,
                        reason: values.reason,
                      });
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </FormSection>
      )}
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {"Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          loading={submitting}
        >
          {"Convert Inquiry"}
        </Button>
      </ButtonMenu>
    </FormWrapper>
  );
};
